import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ReplayIcon from "@mui/icons-material/Replay";
import CloseIcon from "@mui/icons-material/Close";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import BrushIcon from "@mui/icons-material/Brush";
import VideoSettingsIcon from "@mui/icons-material/VideoSettings";
import MRSSThumbnailCreator from "../../components/MRSSThumbnailCreator";
import VideoPlayer from "./VideoPlayer";
import {
  APPROVAL_STATUS,
  PUSH_STATUS,
  RENDER_STATUS,
  StatusChip,
  VIDEO_SOURCE_META,
  absoluteTime,
  getRowState,
  relativeTime,
} from "./constants";

function MetaItem({ label, value, title }) {
  return (
    <Box minWidth={0}>
      <Typography
        variant="caption"
        sx={{ display: "block", fontSize: 9.5, fontWeight: 700, letterSpacing: 0.7, textTransform: "uppercase", color: "#94a3b8" }}
      >
        {label}
      </Typography>
      <Typography variant="caption" sx={{ display: "block", color: "#334155", fontWeight: 500 }} noWrap title={title}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

/**
 * Full-size review of a single push: the video (or why it isn't playable
 * yet), everything known about it, and the approve/reject decision itself.
 *
 * A replacement thumbnail staged here is applied only by Approve — the base64
 * travels in that POST (AdminPortalPushApproveView), so closing the dialog
 * changes nothing. Same "stage now, apply on Approve" pattern as
 * QueuePreview.js ("Video Queue").
 */
export default function PreviewDialog({ row, isAdmin, busyId, onClose, onApprove, onReject, onRetry }) {
  const [staged, setStaged] = useState({ base64: null, preview: null });
  const [editorOpen, setEditorOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Switching rows must not carry a thumbnail staged for a different video.
  useEffect(() => {
    setStaged({ base64: null, preview: null });
  }, [row?.id]);

  const open = Boolean(row);
  const s = row ? getRowState(row, isAdmin) : null;
  const busy = row ? busyId === row.id : false;
  const source = row ? VIDEO_SOURCE_META[row.video_source] || VIDEO_SOURCE_META.ORIGINAL : null;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setStaged({ base64: dataUrl.split(",")[1], preview: dataUrl });
    };
    reader.readAsDataURL(file);
  };

  const handleFromEditor = (base64) => {
    const preview = base64.startsWith("data:") ? base64 : `data:image/jpeg;base64,${base64}`;
    const raw = base64.startsWith("data:") ? base64.split(",")[1] : base64;
    setStaged({ base64: raw, preview });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1.25 }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
            <Box minWidth={0}>
              <Typography variant="h6" fontWeight={700} noWrap sx={{ fontSize: 18 }}>
                {s?.title}
              </Typography>
              <Typography variant="caption" sx={{ color: "#94a3b8", fontFamily: "monospace" }}>
                {s?.key}
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
            <StatusChip map={APPROVAL_STATUS} value={row?.approval_status} />
            <StatusChip map={PUSH_STATUS} value={row?.status} />
            {s?.isEdited && row?.render_status && <StatusChip map={RENDER_STATUS} value={row.render_status} />}
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          {row && (
            <>
              {s.renderBlocksApproval ? (
                // Mirrors QueuePreview.js ("Video Queue")'s render-in-progress
                // treatment: no numeric percentage exists anywhere (the renderer's
                // own progress isn't polled/stored — only a rendering/complete/
                // failed status), so this is the same dimmed-thumbnail +
                // "Render in progress…" overlay used there, not a fabricated bar.
                <Box sx={{ position: "relative", bgcolor: "#0b1120", borderRadius: 2, overflow: "hidden", minHeight: 260 }}>
                  {row.thumbnail_url && (
                    <Box
                      component="img"
                      src={row.thumbnail_url}
                      alt=""
                      sx={{ width: "100%", maxHeight: "58vh", objectFit: "contain", display: "block", mx: "auto", opacity: 0.32 }}
                    />
                  )}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      py: 4,
                      textAlign: "center",
                      px: 3,
                    }}
                  >
                    {s.renderFailed ? (
                      <>
                        <VideoSettingsIcon sx={{ fontSize: 40, color: "#dc2626" }} />
                        <Typography variant="body2" sx={{ color: "#f87171" }} fontWeight={700}>
                          Render failed
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#fca5a5" }}>
                          Reject and resubmit with a fixed template/payload.
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CircularProgress size={32} sx={{ color: "#d97706" }} />
                        <Typography variant="body2" sx={{ color: "#fbbf24" }} fontWeight={700} mt={0.5}>
                          Render in progress…
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#d97706" }}>
                          This auto-refreshes every 30s — the preview appears here once the render finishes.
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              ) : row.preview_url ? (
                <VideoPlayer key={row.id} src={row.preview_url} poster={row.thumbnail_url} autoPlay />
              ) : row.thumbnail_url ? (
                <>
                  <Box
                    component="img"
                    src={row.thumbnail_url}
                    alt=""
                    sx={{ width: "100%", maxHeight: "58vh", objectFit: "contain", borderRadius: 2, bgcolor: "#0b1120" }}
                  />
                  <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
                    No video preview available yet — showing the thumbnail only.
                  </Alert>
                </>
              ) : (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  Preview not available.
                </Alert>
              )}

              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#f8fafc",
                  border: "1px solid #eef2f6",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                  gap: 1.5,
                }}
              >
                <MetaItem label="Target feed" value={s.target} title={s.target} />
                <MetaItem
                  label="Group"
                  value={`${row.admin_portal_group_label || "—"} (${row.admin_portal_group_type || "—"})`}
                />
                <MetaItem label="Video source" value={source.label} />
                <MetaItem label="Send mode" value={row.is_force_push ? "Force" : "Normal (queued)"} />
                <MetaItem
                  label="Created"
                  value={row.created_date ? `${relativeTime(row.created_date)}` : "—"}
                  title={absoluteTime(row.created_date)}
                />
                <MetaItem label="Submitted by" value={row.pushed_by} />
                {row.approved_at && (
                  <MetaItem
                    label={row.approval_status === "REJECTED" ? "Rejected" : "Approved"}
                    value={`${relativeTime(row.approved_at)}${row.approved_by ? ` · ${row.approved_by}` : ""}`}
                    title={absoluteTime(row.approved_at)}
                  />
                )}
              </Box>

              {row.rejection_reason && (
                <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                  {row.rejection_reason}
                </Alert>
              )}
              {row.error_message && (
                <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                  {row.error_message}
                </Alert>
              )}

              {isAdmin && s.isPending && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ textTransform: "uppercase", letterSpacing: 0.7, fontSize: 9.5, color: "#94a3b8", display: "block", mb: 1 }}
                  >
                    Change thumbnail (optional)
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    <Box sx={{ position: "relative", flexShrink: 0 }}>
                      {staged.preview || row.thumbnail_url ? (
                        <Box
                          component="img"
                          src={staged.preview || row.thumbnail_url}
                          alt=""
                          sx={{
                            width: 80,
                            height: 50,
                            borderRadius: 1.5,
                            objectFit: "cover",
                            border: staged.preview ? "2px solid" : "1px solid",
                            borderColor: staged.preview ? "primary.main" : "#e2e8f0",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 80,
                            height: 50,
                            borderRadius: 1.5,
                            bgcolor: "#f1f5f9",
                            border: "1px dashed #cbd5e1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <AddPhotoAlternateIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                        </Box>
                      )}
                      {staged.preview && (
                        <IconButton
                          size="small"
                          onClick={() => setStaged({ base64: null, preview: null })}
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            bgcolor: "error.main",
                            color: "white",
                            width: 18,
                            height: 18,
                            "&:hover": { bgcolor: "error.dark" },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      )}
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<AddPhotoAlternateIcon fontSize="small" />}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e2e8f0" }}
                    >
                      Upload
                    </Button>
                    <Tooltip
                      title={row.preview_url ? "Scrub the video and capture a frame as thumbnail" : "No video available yet to capture a frame from"}
                    >
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          startIcon={<BrushIcon fontSize="small" />}
                          onClick={() => setEditorOpen(true)}
                          disabled={!row.preview_url}
                          sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e2e8f0" }}
                        >
                          Capture from video
                        </Button>
                      </span>
                    </Tooltip>
                    {staged.preview && (
                      <Typography variant="caption" color="primary.main" fontWeight={700}>
                        Staged — replaces the thumbnail when you approve
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.75 }}>
          <Button onClick={onClose} variant="outlined" color="inherit" sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e2e8f0" }}>
            Close
          </Button>
          <Box flex={1} />
          {isAdmin && s?.isPending && (
            <>
              <Button
                onClick={() => { const id = row.id; onClose(); onReject(id); }}
                variant="outlined"
                color="error"
                startIcon={<ThumbDownIcon />}
                disabled={busy}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Reject
              </Button>
              <Tooltip title={s.renderBlocksApproval ? s.approveDisabledReason : ""}>
                <span>
                  <Button
                    onClick={() => onApprove(row.id, staged.base64)}
                    variant="contained"
                    disableElevation
                    startIcon={<ThumbUpIcon />}
                    disabled={busy || !s.canApprove}
                    sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" } }}
                  >
                    {staged.base64 ? "Approve & save thumbnail" : "Approve"}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          {isAdmin && s?.hasFailed && (
            <Button
              onClick={() => { const id = row.id; onClose(); onRetry(id); }}
              variant="contained"
              disableElevation
              startIcon={<ReplayIcon />}
              disabled={busy}
              sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#f59e0b", "&:hover": { bgcolor: "#d97706" } }}
            >
              Retry
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <MRSSThumbnailCreator
        open={editorOpen}
        setOpen={setEditorOpen}
        url={row?.preview_url || ""}
        onSave={handleFromEditor}
      />
    </>
  );
}
